<template>
  <ModalComponent
    :submitButtonText="$t('portfolios.confirm_move')"
    :submit="handleMove"
    :submitDisabled="
      !selectedTargetId || !selection.hasSelectedItems || isProcessing
    "
    :hideSubmitButton="true"
    size="xl"
    @close="close"
    @setForceToggle="forceToggle = $event"
  >
    <template #title>
      <FontAwesomeIcon icon="fas fa-exchange-alt" class="text-primary me-2" />
      {{ $t("portfolios.move_modal_title") }}
    </template>
    <template #body>
      <p class="text-muted mb-3">
        {{ $t("portfolios.move_modal_desc") }}
      </p>

      <!-- Source -->
      <div class="card bg-light border-primary-subtle mb-3">
        <div class="card-body p-3">
          <small class="text-uppercase text-primary fw-bold">
            {{ $t("portfolios.source_portfolio") }}
          </small>
          <h6 class="fw-bold text-primary mb-1 text-break">
            {{ sourcePortfolio?.name }}
          </h6>
          <div class="small text-muted">
            {{
              $t("portfolios.volume_and_items", {
                volume: sourcePortfolio?.total_volume ?? 0,
                count: sourcePortfolio?.items.length ?? 0,
              })
            }}
          </div>
        </div>
      </div>

      <!-- Arrow -->
      <div class="text-center text-muted mb-3">
        <FontAwesomeIcon icon="fas fa-arrow-down" size="lg" />
      </div>

      <!-- Target Selection -->
      <div class="mb-4">
        <label class="form-label fw-bold small text-uppercase text-muted mb-1">
          {{ $t("portfolios.move_target") }}
        </label>
        <select
          v-model="selectedTargetId"
          class="form-select form-select-lg"
          :class="{
            'border-primary': selectedTargetId,
            'text-muted': !selectedTargetId,
          }"
        >
          <option :value="null" disabled selected>
            {{ $t("portfolios.select_move_target") }}
          </option>
          <option
            v-for="target in availableTargets"
            :key="target.id"
            :value="target.id"
          >
            {{ target.name }} ({{ target.total_volume }} MWh)
          </option>
        </select>
      </div>

      <!-- Item selection table -->
      <PortfolioItemsTable
        v-if="moveableItems.length > 0"
        :selection="selection"
        :items="moveableItems"
        :isForAdmin="true"
        :defaultVisibleColumns="PORTFOLIO_CARD_COLUMNS"
        :hideActionBar="true"
        storageContext="move-items-modal"
        tableSize="compact"
      />
    </template>
    <template #footer>
      <div class="d-grid gap-2 w-100">
        <button
          type="button"
          class="btn btn-primary btn-lg"
          :disabled="
            !selectedTargetId || !selection.hasSelectedItems || isProcessing
          "
          @click="handleMove"
        >
          <span
            v-if="isProcessing"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          {{ $t("portfolios.confirm_move") }}
        </button>
        <button
          type="button"
          class="btn btn-link text-muted text-decoration-none"
          @click="close"
        >
          {{ $t("common.cancel") }}
        </button>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import { isItemWaitingForSale } from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { PORTFOLIO_CARD_COLUMNS } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import type { PortfolioGroup } from "../types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const model = defineModel<boolean>({ required: true });

const props = defineProps<{
  sourcePortfolio: PortfolioGroup | null;
  availableTargets: PortfolioGroup[];
}>();

const emit = defineEmits<{
  success: [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const selection = usePortfolioSelection({
  policy: isItemWaitingForSale,
});

const forceToggle = ref<(value: boolean) => void>();
const selectedTargetId = ref<number | null>(null);
const isProcessing = ref(false);

const moveableItems = computed(() => {
  if (!props.sourcePortfolio?.items?.length) return [];
  return props.sourcePortfolio.items.filter(isItemWaitingForSale);
});

const close = () => {
  selection.clearSelection();
  model.value = false;
};

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen) {
    selection.clearSelection();
    selectedTargetId.value = null;
    isProcessing.value = false;
  }
});

const handleMove = async () => {
  if (
    !props.sourcePortfolio ||
    !selectedTargetId.value ||
    !selection.hasSelectedItems
  )
    return;

  isProcessing.value = true;
  try {
    const moveableIds = new Set(moveableItems.value.map((i) => i.id));
    const itemIds = selection.selectedItems
      .map((i) => i.id)
      .filter((id) => moveableIds.has(id));
    if (itemIds.length === 0) return;
    await goSolidApi.post(`/api/portfolio/${props.sourcePortfolio.id}/move`, {
      target_portfolio_id: selectedTargetId.value,
      item_ids: itemIds,
    });
    globalSuccessHandler(t("common.success"), t("common.saved_successfully"));
    selection.clearSelection();
    emit("success");
    model.value = false;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
};
</script>
